import React, { useCallback, useEffect, useState } from 'react'
import { get, direct, queue } from './api'
import { useRoute, go } from './router'
import {
  SaveBadge, Field, LookupSelect, Panel, Thumb, NotCaptured, Empty, Score,
  PresenceCell, score, RATING_LABELS, Lightbox, ConfigRow, type Presence,
} from './ui'

type Row = Record<string, any>
const byOrder = (a: Row, b: Row) => (a.display_order ?? 0) - (b.display_order ?? 0) || String(a.name).localeCompare(String(b.name))

// ---------------------------------------------------------------- shell

interface Boot {
  projects: Row[]; project: Row | null; brands: Row[]; flows: Row[]; features: Row[]
  steps: Row[]; attributes: Row[]; issues: Row[]; counts: Row; imgCounts: Row[]; store: string
}

export const App: React.FC = () => {
  const route = useRoute()
  const [authed, setAuthed] = useState<boolean | null>(null)
  const [boot, setBoot] = useState<Boot | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => { get<{ authed: boolean }>('/api/auth/status').then(r => setAuthed(r.authed)).catch(() => setAuthed(false)) }, [])

  const reload = useCallback(async (pid?: string) => {
    try {
      const b = await get<Boot>('/api/bootstrap' + (pid ? `?project=${pid}` : ''))
      setBoot(b); setErr('')
    } catch (e: any) { setErr(e.message) }
  }, [])

  useEffect(() => { if (authed) void reload(localStorage.getItem('vantage-project') ?? undefined) }, [authed, reload])
  useEffect(() => { if (boot?.project) localStorage.setItem('vantage-project', boot.project.id) }, [boot?.project?.id])

  if (authed === null) return <div className="empty">Loading…</div>
  if (!authed) return <Login onIn={() => setAuthed(true)} />
  if (err) return <div className="main"><div className="banner">Could not load: {err}</div></div>
  if (!boot) return <div className="empty">Loading…</div>

  const p = boot.project
  const ctx = { boot, reload }

  let screen: React.ReactNode = <Projects {...ctx} />
  if (!p && route.name !== '') screen = <Projects {...ctx} />
  else if (route.name === '' ) screen = p ? <ProjectHome {...ctx} /> : <Projects {...ctx} />
  else if (route.name === 'projects') screen = <Projects {...ctx} />
  else if (route.name === 'upload') screen = <Upload {...ctx} />
  else if (route.name === 'untagged') screen = <Untagged {...ctx} />
  else if (route.name === 'brand') screen = <BrandPage {...ctx} brandId={route.parts[1]} flowId={route.parts[2]} />
  else if (route.name === 'compare') screen = <Comparison {...ctx} flowId={route.parts[1]} />
  else if (route.name === 'config') screen = <FlowConfig {...ctx} flowId={route.parts[1]} />
  else if (route.name === 'issues') screen = <Issues {...ctx} />
  else if (route.name === 'image') screen = <ImagePage {...ctx} imageId={route.parts[1]} />
  else if (route.name === 'removed') screen = <Removed {...ctx} />
  else if (route.name === 'manage') screen = <Manage {...ctx} />

  return <>
    <Topbar boot={boot} reload={reload} />
    {screen}
  </>
}

const Topbar: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const [theme, setTheme] = useState(document.documentElement.getAttribute('data-theme') ?? 'light')
  const flip = () => {
    const t = theme === 'dark' ? 'light' : 'dark'
    document.documentElement.setAttribute('data-theme', t)
    localStorage.setItem('vantage-theme', t); setTheme(t)
  }
  return (
    <div className="topbar">
      <span className="wordmark" style={{ cursor: 'pointer' }} onClick={() => go('')}>VANTAGE<span className="d">.</span></span>
      <span className="sep" />
      <span className="crumb">
        <a onClick={() => go('projects')} style={{ cursor: 'pointer' }}>Projects</a>
        {boot.project && <> / <b>{boot.project.name}</b></>}
      </span>
      <span className="spacer" />
      <SaveBadge />
      {boot.projects.length > 1 && (
        <select style={{ width: 170 }} value={boot.project?.id ?? ''} onChange={e => reload(e.target.value)}>
          {boot.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      <a className="btn" href="/api/export">Export everything</a>
      <button className="ibtn" title="Theme" onClick={flip}>{theme === 'dark' ? '☀' : '☾'}</button>
    </div>
  )
}

// ---------------------------------------------------------------- S0 login

const Login: React.FC<{ onIn: () => void }> = ({ onIn }) => {
  const [v, setV] = useState(''); const [e, setE] = useState(''); const [busy, setBusy] = useState(false)
  const submit = async () => {
    setBusy(true); setE('')
    try { await direct('/api/auth/login', 'POST', { passphrase: v }); onIn() }
    catch (x: any) { setE(x.message) } finally { setBusy(false) }
  }
  return (
    <div className="login"><div className="box">
      <div className="wordmark" style={{ textAlign: 'center', marginBottom: 3 }}>VANTAGE<span className="d">.</span></div>
      <div className="faint" style={{ textAlign: 'center', fontSize: 12, marginBottom: 20 }}>Visual competitive research</div>
      <label className="lbl">Passphrase</label>
      <input type="password" autoFocus value={v} onChange={ev => setV(ev.target.value)}
        onKeyDown={ev => { if (ev.key === 'Enter') void submit() }} />
      {e && <div className="chip err" style={{ marginTop: 10, width: '100%', justifyContent: 'center' }}>{e}</div>}
      <button className="btn primary" style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}
        disabled={busy} onClick={() => void submit()}>{busy ? 'Checking…' : 'Enter'}</button>
      <div className="faint" style={{ fontSize: 11, textAlign: 'center', marginTop: 13 }}>
        Remembered on this device for 30 days</div>
    </div></div>
  )
}

// ---------------------------------------------------------------- S1 projects

const Projects: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const [adding, setAdding] = useState(false); const [name, setName] = useState('')
  const create = async () => {
    if (!name.trim()) return
    const r = await direct<{ row: Row }>('/api/create', 'POST', { table: 'projects', name: name.trim() })
    setName(''); setAdding(false); reload(r.row.id)
  }
  return <div className="main">
    <div className="phead">
      <div style={{ flex: 1 }}><h1>Research projects</h1>
        <div className="desc">Each project is its own set of brands, flows and attributes.</div></div>
      <button className="btn primary" onClick={() => setAdding(true)}>+ New project</button>
    </div>
    {adding && <div className="card" style={{ padding: 14, marginBottom: 14, display: 'flex', gap: 8 }}>
      <input autoFocus placeholder="Project name" value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') void create() }} />
      <button className="btn primary" onClick={() => void create()}>Create</button>
      <button className="btn ghost" onClick={() => setAdding(false)}>Cancel</button>
    </div>}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 14 }}>
      {boot.projects.map(p => (
        <div key={p.id} className="door" onClick={() => { reload(p.id); go('') }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <h3 style={{ flex: 1 }}>{p.name} <span className="arw">→</span></h3>
            <button className="ibtn" title="Delete project (recoverable)" onClick={async e => {
              e.stopPropagation()
              if (!confirm(`Delete "${p.name}"? Recoverable from the change log.`)) return
              await direct(`/api/row/projects/${p.id}`, 'DELETE'); reload()
            }}>✕</button>
          </div>
          <p>{p.description || <span className="faint">No description</span>}</p>
          {boot.project?.id === p.id && <div className="ftr">
            <span>{boot.brands.length} brands</span><span>·</span>
            <span>{boot.flows.length} flows</span><span>·</span>
            <span>{boot.counts?.images ?? 0} images</span></div>}
        </div>
      ))}
      {!boot.projects.length && <Empty>No projects yet. Create one to begin.</Empty>}
    </div>
  </div>
}

// ---------------------------------------------------------------- S2 project home

const ProjectHome: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const p = boot.project!
  const c = boot.counts ?? {}
  const imgFor = (bid: string) => boot.imgCounts.find(x => x.brand_id === bid) ?? { n: 0, nf: 0 }
  const flowsReady = boot.flows.filter(f => boot.attributes.some(a => a.flow_id === f.id)).length
  const patch = (field: string, v: string) =>
    queue(`/api/row/projects/${p.id}`, 'PATCH', { [field]: v }).then(() => reload(p.id))

  return <div className="main">
    <div className="phead">
      <div style={{ flex: 1, maxWidth: 640 }}>
        <Field value={p.name} onCommit={v => void patch('name', v)} />
        <div style={{ marginTop: 7 }}>
          <Field value={p.description} onCommit={v => void patch('description', v)} placeholder="Describe this research…" />
        </div>
        <div style={{ display: 'flex', gap: 7, marginTop: 11, flexWrap: 'wrap' }}>
          <span className="chip">{boot.brands.length} brands</span>
          <span className="chip">{boot.flows.length} flows</span>
          <span className="chip">{c.images ?? 0} images</span>
          <span className="chip quiet">{flowsReady} of {boot.flows.length} flows have attributes</span>
        </div>
      </div>
      <button className="btn" onClick={() => go('removed')}>Removed</button>
      <button className="btn" onClick={() => go('manage')}>Configure</button>
      <button className="btn primary" onClick={() => go('upload')}>+ Upload</button>
    </div>

    <div className="grid2" style={{ marginBottom: 28 }}>
      <div className="door" onClick={() => go(`brand/${boot.brands[0]?.id ?? ''}`)}>
        <div className="ic">◫</div>
        <h3>Explore brand <span className="arw">→</span></h3>
        <p>One brand, every flow it appears in, judged against the same attributes.</p>
        <div className="ftr"><span>{boot.brands.length} brands</span></div>
      </div>
      <div className="door" onClick={() => go(`compare/${boot.flows.find(f => boot.attributes.some(a => a.flow_id === f.id))?.id ?? boot.flows[0]?.id ?? ''}`)}>
        <div className="ic">⊞</div>
        <h3>Explore comparison <span className="arw">→</span></h3>
        <p>One flow, every brand side by side. The comparison surface.</p>
        <div className="ftr"><span>{flowsReady} of {boot.flows.length} flows ready</span></div>
      </div>
    </div>

    <div className="slab"><h2>Needs attention</h2><span className="hint">each links to the queue that clears it</span></div>
    <div className="grid4" style={{ marginBottom: 28 }}>
      <div className={`tile${c.untagged ? ' flag' : ''}`} onClick={() => go('untagged')}>
        <div className="v">{c.untagged ?? 0}</div><div className="k">Untagged images →</div></div>
      <div className={`tile${c.needsReview ? ' flag' : ''}`} onClick={() => go(`compare/${boot.flows.find(f => boot.attributes.some(a => a.flow_id === f.id))?.id ?? ''}`)}>
        <div className="v">{c.needsReview ?? 0}</div><div className="k">Cells need review →</div></div>
      <div className="tile" onClick={() => go(`config/${boot.flows[0]?.id ?? ''}`)}>
        <div className="v">{boot.flows.length - flowsReady}</div><div className="k">Flows without attributes →</div></div>
      <div className="tile" onClick={() => go('issues')}>
        <div className="v">{c.openIssues ?? 0}</div><div className="k">Open issues →</div></div>
    </div>
    {!!c.removed && <div style={{ marginTop: -16, marginBottom: 24 }}>
      <span className="faint" style={{ fontSize: 11.5 }}>{c.removed} removed image{c.removed === 1 ? '' : 's'} · </span>
      <span className="more" style={{ fontSize: 11.5 }} onClick={() => go('removed')}>view and restore →</span>
    </div>}

    <div className="slab"><h2>Brands</h2><span className="hint">order is used in every comparison</span></div>
    <table className="tbl">
      <thead><tr><th style={{ width: 24 }} /><th>Brand</th><th style={{ width: 70 }}>Images</th>
        <th style={{ width: 62 }}>Flows</th><th style={{ width: 150 }}>Roll-up</th><th>Note</th></tr></thead>
      <tbody>
        {[...boot.brands].sort(byOrder).map(b => {
          const ic = imgFor(b.id)
          return <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => go(`brand/${b.id}`)}>
            <td className="grip">⠿</td>
            <td style={{ fontWeight: 600 }}>{b.name}</td>
            <td className="num">{ic.n}</td><td className="num">{ic.nf}</td>
            <td><span className="na">—</span><div className="base">open the brand to score</div></td>
            <td className="muted" style={{ fontSize: 11.5 }}>{b.note}</td>
          </tr>
        })}
        {!boot.brands.length && <tr><td colSpan={6}><div className="empty">No brands yet — upload an image to create one.</div></td></tr>}
      </tbody>
    </table>
  </div>
}

// ---------------------------------------------------------------- S3 upload

const Upload: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const p = boot.project!
  const [files, setFiles] = useState<File[]>([])
  const [tags, setTags] = useState<Row[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [zoom, setZoom] = useState<{ src: string; name: string } | null>(null)
  const [shotH, setShotH] = useState(260)
  const [allBrand, setAllBrand] = useState<string | null>(null)
  const [allFlow, setAllFlow] = useState<string | null>(null)
  const [previews, setPreviews] = useState<string[]>([])

  const addFiles = (fl: FileList | null) => {
    if (!fl) return
    const arr = [...fl]
    setFiles(f => [...f, ...arr])
    setPreviews(p => [...p, ...arr.map(f => URL.createObjectURL(f))])
    setTags(t => [...t, ...arr.map(() => ({ brand_id: null, flow_id: null, step_id: null }))])
  }
  const setTag = (i: number, patch: Row) => setTags(t => t.map((x, j) => j === i ? { ...x, ...patch } : x))
  const applyAll = (patch: Row) => setTags(t => t.map(x => ({ ...x, ...patch })))

  const mkLookup = (table: string, extra: Row = {}) => async (name: string) => {
    const r = await direct<{ row: Row }>('/api/create', 'POST', { table, name, project_id: p.id, ...extra })
    await reload(p.id); return r.row
  }

  const derived = (i: number) => {
    const t = tags[i]; const f = files[i]
    const brand = boot.brands.find(b => b.id === t.brand_id)?.name
    const flow = boot.flows.find(x => x.id === t.flow_id)?.name
    const step = boot.steps.find(s => s.id === t.step_id)?.name
    if (!brand || !flow || !step) return f.name
    // Mirrors token() in functions/_lib/core.ts. An empty joiner must not be
    // fed to a RegExp — /^+|+$/ is a SyntaxError and used to blank the page.
    const tok = (v: string, j = '-') => {
      const body = v.replace(/[^A-Za-z0-9]+/g, j)
      if (!j) return body
      const esc = j.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return body.replace(new RegExp(`^(?:${esc})+|(?:${esc})+$`, 'g'), '')
    }
    const n = tags.slice(0, i).filter(x => x.step_id && x.step_id === t.step_id).length + 1
    const ext = (f.name.match(/\.([A-Za-z0-9]+)$/)?.[1] ?? 'png').toLowerCase()
    return `${tok(brand, '')}_${tok(flow)}_${tok(step)}${n > 1 ? `_${n}` : ''}.${ext}`
  }

  const send = async () => {
    const missing = tags.findIndex(t => !t.brand_id)
    if (missing >= 0) { setMsg(`Row ${missing + 1} has no brand — brand is the one required tag.`); return }
    setBusy(true); setMsg('')
    try {
      // group by identical tags so each POST is one step
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData()
        fd.append('project_id', p.id)
        fd.append('brand_id', tags[i].brand_id)
        if (tags[i].flow_id) fd.append('flow_id', tags[i].flow_id)
        if (tags[i].step_id) fd.append('step_id', tags[i].step_id)
        fd.append('file', files[i])
        await direct('/api/upload', 'POST', fd)
      }
      previews.forEach(URL.revokeObjectURL)
      setFiles([]); setTags([]); setPreviews([]); await reload(p.id)
      setMsg(`Uploaded. ${files.length} image${files.length > 1 ? 's' : ''} stored.`)
    } catch (e: any) { setMsg('Upload failed: ' + e.message) } finally { setBusy(false) }
  }

  return <div className="main" style={{ maxWidth: 'none' }}>
    <div className="phead"><div style={{ flex: 1 }}><h1>Upload</h1>
      <div className="desc">Brand is the only required tag. Anything untagged lands in the untagged queue.</div></div>
      <button className="btn primary" disabled={!files.length || busy} onClick={() => void send()}>
        {busy ? 'Uploading…' : `Upload ${files.length || ''}`}</button>
    </div>

    {msg && <div className="banner">{msg}</div>}

    <label className="ghostcard" style={{ height: 68, marginBottom: 14, cursor: 'pointer',
      color: 'var(--muted)', aspectRatio: 'auto' }}>
      <input type="file" multiple accept="image/*" style={{ display: 'none' }}
        onChange={e => addFiles(e.target.files)} />
      Click to choose screenshots · PNG or JPG · original resolution kept
    </label>

    {!!files.length && <>
      <div className="card" style={{ padding: 11, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: 170 }}><LookupSelect label="Apply brand to all" value={allBrand} options={boot.brands}
          onChange={id => { setAllBrand(id); if (id) applyAll({ brand_id: id }) }} onCreate={mkLookup('brands')} /></div>
        <div style={{ width: 170 }}><LookupSelect label="Apply flow to all" value={allFlow} options={boot.flows}
          onChange={id => { setAllFlow(id); if (id) applyAll({ flow_id: id, step_id: null }) }} onCreate={mkLookup('flows')} /></div>
        <span className="faint" style={{ fontSize: 11.5, paddingBottom: 8 }}>then override any card below</span>
        <span style={{ flex: 1 }} />
        <span className="faint" style={{ fontSize: 11.5, paddingBottom: 8 }}>{files.length} queued · preview size</span>
        <input type="range" min={200} max={620} step={20} value={shotH} style={{ width: 130, marginBottom: 6 }}
          onChange={e => setShotH(Number(e.target.value))} />
      </div>

      <div style={{ display: 'grid', gap: 14,
        gridTemplateColumns: `repeat(auto-fill,minmax(${Math.round(shotH * 0.462) + 250}px,1fr))` }}>
        {files.map((f, i) => {
          const t = tags[i]
          const steps = boot.steps.filter(x => x.flow_id === t.flow_id)
          const url = previews[i]
          const name = derived(i)
          const tagged = Boolean(t.step_id && t.flow_id)
          return <div key={i} className="card" style={{ padding: 12, display: 'flex', gap: 12 }}>
            <img src={url} title="Click to view full size" onClick={() => setZoom({ src: url, name: f.name })}
              style={{ width: Math.round(shotH * 0.462), height: shotH, flex: 'none', objectFit: 'cover',
                objectPosition: 'top', borderRadius: 7, border: '1px solid var(--line)', cursor: 'zoom-in' }} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                <div className="mono faint" style={{ fontSize: 10.5, flex: 1, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                <button className="ibtn" style={{ width: 22, height: 22, fontSize: 12 }}
                  onClick={() => {
                  URL.revokeObjectURL(previews[i])
                  setFiles(x => x.filter((_, j) => j !== i))
                  setTags(x => x.filter((_, j) => j !== i))
                  setPreviews(x => x.filter((_, j) => j !== i))
                }}>✕</button>
              </div>
              <LookupSelect label="Brand *" value={t.brand_id} options={boot.brands} required allowNone={false}
                onChange={id => setTag(i, { brand_id: id })} onCreate={mkLookup('brands')} />
              <LookupSelect label="Flow" value={t.flow_id} options={boot.flows}
                onChange={id => setTag(i, { flow_id: id, step_id: null })} onCreate={mkLookup('flows')} />
              <LookupSelect label="Step" value={t.step_id} options={steps}
                onChange={id => setTag(i, { step_id: id })}
                onCreate={t.flow_id ? mkLookup('steps', { flow_id: t.flow_id }) : undefined} />
              <div style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid var(--line-2)' }}>
                <div className="lbl" style={{ marginBottom: 2 }}>Filename</div>
                <div className="mono" style={{ fontSize: 10.5, wordBreak: 'break-all',
                  color: tagged ? 'var(--ink)' : 'var(--warn)' }}>{name}</div>
                {!tagged && <div className="faint" style={{ fontSize: 10.5, marginTop: 2 }}>→ untagged queue</div>}
              </div>
            </div>
          </div>
        })}
      </div>
    </>}
    {zoom && <Lightbox src={zoom.src} name={zoom.name} onClose={() => setZoom(null)} />}
  </div>
}

// ---------------------------------------------------------------- S4 untagged

const Untagged: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const p = boot.project!
  const [imgs, setImgs] = useState<Row[]>([])
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [open, setOpen] = useState<string | null>(null)
  const [bulk, setBulk] = useState<Row>({ flow_id: null, step_id: null })
  const [brand, setBrand] = useState('')

  const load = useCallback(() => {
    get<{ images: Row[] }>(`/api/images?project=${p.id}&untagged=1&limit=2000`).then(r => setImgs(r.images))
  }, [p.id])
  useEffect(load, [load])

  const shown = brand ? imgs.filter(i => i.brand_id === brand) : imgs
  const toggle = (id: string) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const applyBulk = async () => {
    if (!sel.size || !bulk.step_id) return
    await direct('/api/images/bulk', 'POST', { ids: [...sel], patch: bulk })
    setSel(new Set()); load(); await reload(p.id)
  }
  const mkLookup = (table: string, extra: Row = {}) => async (name: string) => {
    const r = await direct<{ row: Row }>('/api/create', 'POST', { table, name, project_id: p.id, ...extra })
    await reload(p.id); return r.row
  }

  return <div className="main" style={{ maxWidth: 'none' }}>
    <div className="phead"><div style={{ flex: 1 }}><h1>Untagged images</h1>
      <div className="desc">{imgs.length} image{imgs.length === 1 ? '' : 's'} missing a flow, feature or step. Tag them to make them comparable.</div></div>
      <select style={{ width: 150 }} value={brand} onChange={e => setBrand(e.target.value)}>
        <option value="">All brands</option>
        {boot.brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <button className="btn" onClick={() => setSel(new Set(shown.map(i => i.id)))}>Select all</button>
    </div>

    {!!sel.size && <div className="card" style={{ padding: 12, marginBottom: 14, display: 'flex', gap: 9, alignItems: 'flex-end' }}>
      <b style={{ paddingBottom: 8 }}>{sel.size} selected</b>
      <div style={{ width: 160 }}><LookupSelect label="Flow" value={bulk.flow_id} options={boot.flows}
        onChange={id => setBulk(b => ({ ...b, flow_id: id, step_id: null }))} onCreate={mkLookup('flows')} /></div>
      <div style={{ width: 160 }}><LookupSelect label="Step" value={bulk.step_id}
        options={boot.steps.filter(s => s.flow_id === bulk.flow_id)}
        onChange={id => setBulk(b => ({ ...b, step_id: id }))}
        onCreate={bulk.flow_id ? mkLookup('steps', { flow_id: bulk.flow_id }) : undefined} /></div>
      <button className="btn primary" disabled={!bulk.step_id} onClick={() => void applyBulk()}>Tag {sel.size}</button>
      <button className="btn" style={{ borderColor: 'var(--err-line)', color: 'var(--err)' }}
        onClick={async () => {
          if (!confirm(`Remove ${sel.size} image${sel.size > 1 ? 's' : ''}? They stay recoverable under Removed images.`)) return
          await direct('/api/images/delete', 'POST', { ids: [...sel] })
          setSel(new Set()); load(); await reload(p.id)
        }}>Remove {sel.size}</button>
      <button className="btn ghost" onClick={() => setSel(new Set())}>Clear</button>
    </div>}

    {!shown.length ? <Empty>Nothing untagged. Every image has a flow and a step.</Empty> :
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: 12 }}>
        {shown.map(i => <div key={i.id}>
          <Thumb img={i} h={180} sel={sel.has(i.id)} onClick={() => toggle(i.id)} />
          <div className="mono" style={{ fontSize: 10, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {i.original_filename}</div>
          <div style={{ display: 'flex', gap: 5, marginTop: 3, alignItems: 'center' }}>
            <span className="chip" style={{ fontSize: 10 }}>{boot.brands.find(b => b.id === i.brand_id)?.name ?? '—'}</span>
            <span className="btn sm ghost" onClick={() => setOpen(i.id)}>open</span>
          </div>
        </div>)}
      </div>}

    {open && <ImagePanel boot={boot} imageId={open} onClose={() => { setOpen(null); load() }} reload={reload} />}
  </div>
}

// ---------------------------------------------------------------- S5 image detail

const ImageEditor: React.FC<{ boot: Boot; image: Row; onChanged: () => void; reload: (p?: string) => void; onRemoved?: () => void }> =
  ({ boot, image, onChanged, reload, onRemoved }) => {
    const p = boot.project!
    const [confirmDel, setConfirmDel] = useState(false)
    const [zoom, setZoom] = useState(false)
    const [issues, setIssues] = useState<Row[]>(boot.issues.filter(i => i.image_id === image.id))
    const [newIssue, setNewIssue] = useState('')
    const patch = (field: string, v: any) => { void queue(`/api/images/${image.id}`, 'PATCH', { [field]: v }).then(onChanged) }
    const mkLookup = (table: string, extra: Row = {}) => async (name: string) => {
      const r = await direct<{ row: Row }>('/api/create', 'POST', { table, name, project_id: p.id, ...extra })
      await reload(p.id); return r.row
    }
    const addIssue = async () => {
      if (!newIssue.trim()) return
      const r = await direct<{ issue: Row }>('/api/issues', 'POST', {
        project_id: p.id, image_id: image.id, brand_id: image.brand_id,
        flow_id: image.flow_id, step_id: image.step_id, title: newIssue.trim(),
      })
      setIssues(i => [r.issue, ...i]); setNewIssue(''); await reload(p.id)
    }
    const steps = boot.steps.filter(s => s.flow_id === image.flow_id)

    return <>
      <img src={`/${image.r2_key}`} onClick={() => setZoom(true)} title="Click to view full size"
        style={{ width: '100%', borderRadius: 8, border: '1px solid var(--line)', display: 'block', cursor: 'zoom-in' }} />
      {zoom && <Lightbox src={`/${image.r2_key}`} name={image.display_name || image.original_filename} onClose={() => setZoom(false)} />}
      <div className="faint" style={{ fontSize: 11, margin: '6px 0 16px' }}>
        Original resolution{image.width ? ` · ${image.width}×${image.height}` : ''}
        {image.bytes ? ` · ${Math.round(image.bytes / 1024)} KB` : ''} · click to open full size
      </div>

      <div className="slab"><h2>Tags</h2></div>
      <div style={{ display: 'grid', gap: 9, marginBottom: 18 }}>
        <LookupSelect label="Brand" required allowNone={false} value={image.brand_id} options={boot.brands}
          onChange={id => patch('brand_id', id)} onCreate={mkLookup('brands')} />
        <LookupSelect label="Flow" value={image.flow_id} options={boot.flows}
          onChange={id => { patch('flow_id', id); patch('step_id', null) }}
          onCreate={mkLookup('flows')} />
        <LookupSelect label="Step" value={image.step_id} options={steps}
          onChange={id => patch('step_id', id)}
          onCreate={image.flow_id ? mkLookup('steps', { flow_id: image.flow_id }) : undefined} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
          <Field label="Captured on" value={image.captured_on ?? ''} onCommit={v => patch('captured_on', v)} />
          <div><label className="lbl">Shown upfront</label>
            <button className={`btn${image.is_primary ? ' primary' : ''}`} style={{ width: '100%', justifyContent: 'center' }}
              onClick={() => patch('is_primary', image.is_primary ? 0 : 1)}>
              {image.is_primary ? '★ primary' : 'Set as primary'}</button></div>
        </div>
        <Field label="Filename (derived)" value={image.display_name || image.original_filename}
          onCommit={() => {}} readOnly mono />
        <Field label="Caption / comment" textarea value={image.caption ?? ''} onCommit={v => patch('caption', v)}
          placeholder="What this screen shows…" />
      </div>

      <div className="slab"><h2>Issues</h2></div>
      {issues.map(i => (
        <div key={i.id} className="card" style={{ padding: 9, marginBottom: 6 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <b style={{ flex: 1, fontSize: 12.5 }}>{i.title}</b>
            <select style={{ width: 104 }} defaultValue={i.status}
              onChange={e => void queue(`/api/row/issues/${i.id}`, 'PATCH', { status: e.target.value })}>
              {['pending', 'wip', 'done', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        <input placeholder="Report an issue on this screen…" value={newIssue}
          onChange={e => setNewIssue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void addIssue() }} />
        <button className="btn" onClick={() => void addIssue()}>Add</button>
      </div>

      <div className="slab"><h2>Remove</h2></div>
      {confirmDel ? (
        <div className="card" style={{ padding: 12, borderColor: 'var(--err-line)', background: 'var(--err-bg)' }}>
          <div style={{ fontSize: 12.5, marginBottom: 9 }}>
            <b>Remove this image?</b> It disappears from every view. Nothing is destroyed — the record and the
            stored file both survive, and you can put it back from <b>Removed images</b>.
          </div>
          <div style={{ display: 'flex', gap: 7 }}>
            <button className="btn" style={{ borderColor: 'var(--err)', color: 'var(--err)' }}
              onClick={async () => { await direct(`/api/row/images/${image.id}`, 'DELETE'); await reload(p.id); onRemoved?.() }}>
              Remove</button>
            <button className="btn ghost" onClick={() => setConfirmDel(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn" onClick={() => setConfirmDel(true)}>Remove image</button>
      )}

      <div className="slab" style={{ marginTop: 18 }}><h2>Version history</h2></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
        <span><b>v1</b> · {image.captured_on || new Date(image.uploaded_at).toLocaleDateString('en-GB')}</span>
        <span className="chip">current</span>
      </div>
      <div className="faint" style={{ fontSize: 11, marginTop: 4 }}>
        Re-uploading creates v2. Earlier versions are never deleted.</div>
    </>
  }

const ImagePanel: React.FC<{ boot: Boot; imageId: string; onClose: () => void; reload: (p?: string) => void }> =
  ({ boot, imageId, onClose, reload }) => {
    const [img, setImg] = useState<Row | null>(null)
    const load = useCallback(() => {
      get<{ images: Row[] }>(`/api/images?project=${boot.project!.id}&limit=2000`)
        .then(r => setImg(r.images.find(i => i.id === imageId) ?? null))
    }, [imageId, boot.project?.id])
    useEffect(load, [load])
    return <Panel title={img?.display_name || img?.original_filename || 'Image'} onClose={onClose}>
      {img ? <ImageEditor boot={boot} image={img} onChanged={load} reload={reload} onRemoved={onClose} /> : <div className="empty">Loading…</div>}
    </Panel>
  }

const ImagePage: React.FC<{ boot: Boot; imageId: string; reload: (p?: string) => void }> = ({ boot, imageId, reload }) => {
  const [img, setImg] = useState<Row | null>(null)
  const load = useCallback(() => {
    get<{ images: Row[] }>(`/api/images?project=${boot.project!.id}&limit=2000`)
      .then(r => setImg(r.images.find(i => i.id === imageId) ?? null))
  }, [imageId, boot.project?.id])
  useEffect(load, [load])
  if (!img) return <div className="empty">Loading…</div>
  return <div className="main" style={{ maxWidth: 720 }}>
    <div className="phead"><div style={{ flex: 1 }}><h1 style={{ fontSize: 17 }}>
      {img.display_name || img.original_filename}</h1></div>
      <button className="btn ghost" onClick={() => history.back()}>← Back</button></div>
    <ImageEditor boot={boot} image={img} onChanged={load} reload={reload} />
  </div>
}

// ---------------------------------------------------------------- S6/S7 brand page

const BrandPage: React.FC<{ boot: Boot; brandId?: string; flowId?: string; reload: (p?: string) => void }> =
  ({ boot, brandId, flowId, reload }) => {
    const p = boot.project!
    const bid = brandId || boot.brands[0]?.id
    const [data, setData] = useState<any>(null)
    const [open, setOpen] = useState<string | null>(null)
    const load = useCallback(() => {
      if (bid) get(`/api/brand/${bid}?project=${p.id}`).then(setData)
    }, [bid, p.id])
    useEffect(load, [load])

    if (!bid) return <Empty>No brands yet.</Empty>
    if (!data) return <div className="empty">Loading…</div>
    const flows: Row[] = data.flows
    const imgs: Row[] = data.images
    const shownFlows = flowId ? flows.filter(f => f.id === flowId) : flows

    return <div className="main">
      <div className="phead">
        <div style={{ flex: 1 }}>
          <h1>{data.brand?.name}</h1>
          <div className="desc">{imgs.length} images · {new Set(imgs.map(i => i.flow_id).filter(Boolean)).size} flows</div>
        </div>
        <select style={{ width: 170 }} value={bid} onChange={e => go(`brand/${e.target.value}`)}>
          {boot.brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <div style={{ textAlign: 'right', minWidth: 140 }}><Score s={data.brandScore} /></div>
      </div>

      {shownFlows.map(f => {
        const fi = imgs.filter(i => i.flow_id === f.id)
        const steps: Row[] = data.steps.filter((s: Row) => s.flow_id === f.id)
        if (!fi.length && !steps.length) return null
        return <div key={f.id} className="card" style={{ marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 14px',
            background: 'var(--surface-2)', borderBottom: '1px solid var(--line-2)' }}>
            <b>{f.name}</b>
            <span className="chip">{fi.length} images</span>
            <span className="chip">{steps.length} steps</span>
            <span className="spacer" />
            <div style={{ textAlign: 'right' }}><Score s={data.flowScores[f.id]} /></div>
            <button className="btn sm" onClick={() => go(`compare/${f.id}`)}>Compare →</button>
          </div>
          <div style={{ padding: 13 }}>
            <div className="hscroll">
              {steps.map(s => {
                const si = fi.filter(i => i.step_id === s.id)
                const primary = si.find(i => i.is_primary) ?? si[0]
                return <div key={s.id} style={{ flex: 'none' }}>
                  {primary ? <Thumb img={primary} h={180} onClick={() => setOpen(primary.id)} />
                           : <NotCaptured h={180} />}
                  <div className="mono" style={{ fontSize: 10, marginTop: 4 }}>⠿ _{s.flow_sequence}</div>
                  <div style={{ fontSize: 11.5, maxWidth: 120 }}>{s.name}</div>
                  {si.length > 1 && <span className="chip" style={{ fontSize: 10, marginTop: 3 }}>+{si.length - 1} more</span>}
                </div>
              })}
              {fi.filter(i => !i.step_id).map(i => <div key={i.id} style={{ flex: 'none' }}>
                <Thumb img={i} h={180} onClick={() => setOpen(i.id)} />
                <div className="mono" style={{ fontSize: 10, marginTop: 4, color: 'var(--warn)' }}>untagged</div>
              </div>)}
            </div>
          </div>
        </div>
      })}

      {(() => {
        const orphans = imgs.filter(i => !i.flow_id)
        return orphans.length ? <div className="card" style={{ padding: 13, borderStyle: 'dashed' }}>
          <div className="slab"><h2>Unknown flow</h2>
            <span className="hint">{orphans.length} images with no flow — tag them to score them</span>
            <span className="more" onClick={() => go('untagged')}>Untagged queue →</span></div>
          <div className="hscroll">{orphans.map(i =>
            <div key={i.id} style={{ flex: 'none' }}><Thumb img={i} h={150} onClick={() => setOpen(i.id)} /></div>)}
          </div>
        </div> : null
      })()}

      {open && <ImagePanel boot={boot} imageId={open} onClose={() => { setOpen(null); load() }} reload={reload} />}
    </div>
  }

// ---------------------------------------------------------------- S8 comparison

/**
 * One merged table, not two.
 *
 * The first build put steps on the x-axis and brands on the y. That was wrong:
 * brokers name their screens differently, so a flow with 32 step names had 89%
 * of its grid empty even though every broker had screenshots. Steps are a
 * per-brand idea, not a shared axis.
 *
 * Each brand now gets one block: its screenshots in a scrolling strip, with its
 * ratings directly underneath, so you rate while looking at the evidence.
 */
const Comparison: React.FC<{ boot: Boot; flowId?: string; reload: (p?: string) => void }> = ({ boot, flowId, reload }) => {
  const p = boot.project!
  const fid = flowId || boot.flows[0]?.id
  const [d, setD] = useState<any>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [onlyReview, setOnlyReview] = useState(false)
  const [hideEmpty, setHideEmpty] = useState(true)
  const [shotH, setShotH] = useState(() => Number(localStorage.getItem('vantage-shot-h') ?? 320))

  const load = useCallback(() => {
    if (fid) get(`/api/comparison?flow=${fid}&project=${p.id}`).then(setD)
  }, [fid, p.id])
  useEffect(load, [load])
  useEffect(() => { localStorage.setItem('vantage-shot-h', String(shotH)) }, [shotH])

  if (!fid) return <Empty>No flows yet.</Empty>
  if (!d) return <div className="empty">Loading…</div>

  const attrs: Row[] = d.attributes
  const brands: Row[] = d.brands
  const steps: Row[] = d.steps
  const cells: Row[] = d.cells
  const images: Row[] = d.images
  const flow = boot.flows.find(f => f.id === fid)
  const stepName = (id: string | null) => steps.find(s => s.id === id)?.name ?? 'Untagged'

  const cellOf = (aid: string, bid: string) =>
    cells.find(c => c.attribute_id === aid && c.brand_id === bid && !c.step_id)

  const setCell = async (aid: string, bid: string, presence: Presence, rating: number | null) => {
    const existing = cellOf(aid, bid)
    setD((old: any) => ({
      ...old,
      cells: existing
        ? old.cells.map((c: Row) => c === existing ? { ...c, presence, rating, needs_review: 0 } : c)
        : [...old.cells, { attribute_id: aid, brand_id: bid, presence, rating, needs_review: 0, comment: '' }],
    }))
    await queue('/api/assessments', 'PUT', {
      project_id: p.id, brand_id: bid, attribute_id: aid, step_id: null,
      presence, rating, comment: existing?.comment ?? '', needs_review: 0, review_reason: '',
      evidence: existing?.evidence ?? '[]', scope_note: existing?.scope_note ?? '',
    })
  }

  const brandScore = (bid: string) => score(attrs.map(a => {
    const c = cellOf(a.id, bid)
    return { presence: (c?.presence ?? 'uncaptured') as Presence, rating: c?.rating ?? null }
  }))

  const imgsFor = (bid: string) => images.filter(i => i.brand_id === bid)
  let shown = brands
  if (hideEmpty) shown = shown.filter(b => imgsFor(b.id).length || cellOf(attrs[0]?.id, b.id))
  if (onlyReview) shown = shown.filter(b => attrs.some(a => cellOf(a.id, b.id)?.needs_review))

  const W = Math.round(shotH * 0.462)   // phone screenshots are 1179x2556

  return <div className="main" style={{ maxWidth: 'none' }}>
    <div className="phead">
      <div style={{ flex: 1 }}>
        <h1>Compare — {flow?.name}</h1>
        <div className="desc">
          {attrs.length} attributes × {shown.length} brands · {images.length} screenshots.
          Rate each brand against its own evidence.
        </div>
      </div>
      <select style={{ width: 190 }} value={fid} onChange={e => go(`compare/${e.target.value}`)}>
        {boot.flows.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <button className="btn" onClick={() => go(`config/${fid}`)}>Attributes</button>
    </div>

    {!attrs.length && <div className="banner">
      This flow has no attributes yet, so there is nothing to rate.
      <span className="more" style={{ marginLeft: 8, cursor: 'pointer' }} onClick={() => go(`config/${fid}`)}>Define them →</span>
    </div>}

    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
      <button className={`btn sm${onlyReview ? ' primary' : ''}`} onClick={() => setOnlyReview(v => !v)}>Needs review</button>
      <button className={`btn sm${hideEmpty ? ' primary' : ''}`} onClick={() => setHideEmpty(v => !v)}>Hide brands with no evidence</button>
      <span className="spacer" style={{ flex: 1 }} />
      <span className="faint" style={{ fontSize: 11.5 }}>Screenshot size</span>
      <input type="range" min={200} max={620} step={20} value={shotH} style={{ width: 130 }}
        onChange={e => setShotH(Number(e.target.value))} />
    </div>

    {shown.map(b => {
      const bi = imgsFor(b.id)
      return <div key={b.id} className="card" style={{ marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
          background: 'var(--surface-2)', borderBottom: '1px solid var(--line)' }}>
          <b style={{ fontSize: 15, letterSpacing: '-.01em' }}>{b.name}</b>
          <span className="chip">{bi.length} screenshot{bi.length === 1 ? '' : 's'}</span>
          {attrs.some(a => cellOf(a.id, b.id)?.needs_review) && <span className="chip warn">needs review</span>}
          <span className="spacer" style={{ flex: 1 }} />
          <div style={{ textAlign: 'right' }}><Score s={brandScore(b.id)} /></div>
        </div>

        {bi.length ? (
          <div className="hscroll" style={{ padding: '13px 14px 6px' }}>
            {bi.map(i => <div key={i.id} style={{ flex: 'none', width: W }}>
              <Thumb img={i} h={shotH} w={W} onClick={() => setOpen(i.id)} />
              <div style={{ fontSize: 11, marginTop: 5, lineHeight: 1.3 }}>{stepName(i.step_id)}</div>
              {i.caption && <div className="faint" style={{ fontSize: 10.5 }}>{i.caption}</div>}
            </div>)}
          </div>
        ) : (
          <div className="ghostcard" style={{ margin: 14, padding: 18, aspectRatio: 'auto' }}>
            No screenshots captured for this brand in this flow
          </div>
        )}

        {!!attrs.length && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(330px,1fr))',
            gap: 0, borderTop: '1px solid var(--line)' }}>
            {attrs.map(a => {
              const c = cellOf(a.id, b.id)
              return <div key={a.id} style={{ padding: '11px 14px', borderBottom: '1px solid var(--line-2)',
                borderRight: '1px solid var(--line-2)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
                  <b style={{ fontSize: 12.5 }}>{a.name}</b>
                  {c?.needs_review ? <span className="chip warn" style={{ height: 18, fontSize: 10 }} title={c.review_reason}>review</span> : null}
                </div>
                <div className="faint" style={{ fontSize: 10.5, margin: '2px 0 7px', lineHeight: 1.35 }}>{a.description}</div>
                <PresenceCell presence={(c?.presence ?? 'uncaptured') as Presence} rating={c?.rating ?? null}
                  onChange={(pz, r) => void setCell(a.id, b.id, pz, r)} />
              </div>
            })}
          </div>
        )}
      </div>
    })}

    {!shown.length && <Empty>No brands match these filters.</Empty>}
    {open && <ImagePanel boot={boot} imageId={open} onClose={() => { setOpen(null); load() }} reload={reload} />}
  </div>
}

// ---------------------------------------------------------------- S9 flow config

const FlowConfig: React.FC<{ boot: Boot; flowId?: string; reload: (p?: string) => void }> = ({ boot, flowId, reload }) => {
  const p = boot.project!
  const fid = flowId || boot.flows[0]?.id
  const flow = boot.flows.find(f => f.id === fid)
  const attrs = boot.attributes.filter(a => a.flow_id === fid).sort(byOrder)
  const steps = boot.steps.filter(s => s.flow_id === fid).sort((a, b) => a.flow_sequence - b.flow_sequence)
  const [name, setName] = useState(''); const [desc, setDesc] = useState('')
  const [stepName, setStepName] = useState('')
  const [counts, setCounts] = useState<Record<string, number>>({})

  useEffect(() => {
    get<{ images: Row[] }>(`/api/images?project=${p.id}&flow=${fid}&limit=2000`).then(r => {
      const c: Record<string, number> = {}
      for (const i of r.images) if (i.step_id) c[i.step_id] = (c[i.step_id] ?? 0) + 1
      setCounts(c)
    }).catch(() => {})
  }, [fid, p.id, boot.steps.length])

  const refresh = () => reload(p.id)
  const addAttr = async () => {
    if (!name.trim()) return
    await direct('/api/create', 'POST', { table: 'attributes', name: name.trim(), description: desc.trim(), flow_id: fid, project_id: p.id })
    setName(''); setDesc(''); refresh()
  }
  const addStep = async () => {
    if (!stepName.trim()) return
    await direct('/api/create', 'POST', { table: 'steps', name: stepName.trim(), flow_id: fid, project_id: p.id })
    setStepName(''); refresh()
  }
  const move = async (list: Row[], i: number, d: number, table: string, field: string) => {
    const next = [...list]
    const [row] = next.splice(i, 1); next.splice(i + d, 0, row)
    await direct('/api/reorder', 'POST', { table, ids: next.map(x => x.id), field })
    refresh()
  }
  const del = async (table: string, id: string) => { await direct(`/api/row/${table}/${id}`, 'DELETE'); refresh() }
  const rename = async (table: string, id: string, v: string) => {
    await queue(`/api/row/${table}/${id}`, 'PATCH', { name: v }); setTimeout(refresh, 400)
  }
  if (!fid) return <Empty>No flows yet.</Empty>

  return <div className="main" style={{ maxWidth: 1180 }}>
    <div className="phead">
      <div style={{ flex: 1 }}><h1>Configure — {flow?.name}</h1>
        <div className="desc">Attributes are what every brand is rated on. Steps are the stages of this flow,
        in the order they appear everywhere else.</div></div>
      <select style={{ width: 190 }} value={fid} onChange={e => go(`config/${e.target.value}`)}>
        {boot.flows.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <button className="btn" onClick={() => go('manage')}>Brands &amp; flows</button>
      <button className="btn primary" onClick={() => go(`compare/${fid}`)}>Compare →</button>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>
      <div>
        <div className="slab"><h2>Attributes</h2><span className="hint">{attrs.length} · rated per brand</span></div>
        <div className="card" style={{ padding: '4px 14px 12px' }}>
          {attrs.map((a, i) => (
            <ConfigRow key={a.id} name={a.name}
              meta={<div style={{ flex: 1.3, minWidth: 0 }}><Field value={a.description ?? ''}
                onCommit={v => void queue(`/api/row/attributes/${a.id}`, 'PATCH', { description: v })}
                placeholder="What it covers…" /></div>}
              onRename={v => void rename('attributes', a.id, v)}
              onDelete={() => void del('attributes', a.id)}
              onUp={i > 0 ? () => void move(attrs, i, -1, 'attributes', 'display_order') : undefined}
              onDown={i < attrs.length - 1 ? () => void move(attrs, i, 1, 'attributes', 'display_order') : undefined} />
          ))}
          {!attrs.length && <div className="empty">No attributes. This flow's comparison is empty until it has some.</div>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
            <div style={{ width: 190 }}><Field label="New attribute" value={name} onCommit={setName} placeholder="OI analytics" /></div>
            <div style={{ flex: 1 }}><Field label="What it covers" value={desc} onCommit={setDesc} placeholder="OI bars, max pain, PCR" /></div>
            <button className="btn primary" onClick={() => void addAttr()}>Add</button>
          </div>
        </div>
        <div className="banner" style={{ marginTop: 14, background: 'var(--surface-2)', borderColor: 'var(--line)', color: 'var(--muted)' }}>
          <b style={{ color: 'var(--ink)' }}>Sizing test:</b> could two people rate this the same way from the same
          screen? “Market context” is too broad, “max pain on the chain” too atomic, “OI analytics” is right.
        </div>
      </div>

      <div>
        <div className="slab"><h2>Steps</h2><span className="hint">{steps.length} · order drives the filename suffix</span></div>
        <div className="card" style={{ padding: '4px 14px 12px' }}>
          {steps.map((st, i) => (
            <ConfigRow key={st.id} name={st.name} usage={counts[st.id] ?? 0}
              prefix={<span className="mono faint" style={{ fontSize: 11, width: 26 }}>_{st.flow_sequence}</span>}
              onRename={v => void rename('steps', st.id, v)}
              onDelete={() => void del('steps', st.id)}
              onUp={i > 0 ? () => void move(steps, i, -1, 'steps', 'flow_sequence') : undefined}
              onDown={i < steps.length - 1 ? () => void move(steps, i, 1, 'steps', 'flow_sequence') : undefined} />
          ))}
          {!steps.length && <div className="empty">No steps yet.</div>}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
            <div style={{ flex: 1 }}><Field label="New step" value={stepName} onCommit={setStepName} placeholder="Chain-Default-View" /></div>
            <button className="btn primary" onClick={() => void addStep()}>Add</button>
          </div>
        </div>

        <div className="slab" style={{ marginTop: 20 }}><h2>Rating labels</h2></div>
        <table className="tbl"><tbody>
          {[1, 2, 3, 4, 5].map(n => <tr key={n}><td className="num" style={{ width: 24 }}>{n}</td><td>{RATING_LABELS[n]}</td></tr>)}
        </tbody></table>
      </div>
    </div>
  </div>
}

// ---------------------------------------------------------------- manage brands & flows

const Manage: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const p = boot.project!
  const brands = [...boot.brands].sort(byOrder)
  const flows = [...boot.flows].sort(byOrder)
  const [nb, setNb] = useState(''); const [nf, setNf] = useState('')
  const refresh = () => reload(p.id)

  const imgPerBrand = (id: string) => boot.imgCounts.find(x => x.brand_id === id)?.n ?? 0
  const stepsPerFlow = (id: string) => boot.steps.filter(s => s.flow_id === id).length

  const move = async (list: Row[], i: number, d: number, table: string) => {
    const next = [...list]; const [row] = next.splice(i, 1); next.splice(i + d, 0, row)
    await direct('/api/reorder', 'POST', { table, ids: next.map(x => x.id), field: 'display_order' })
    refresh()
  }
  const del = async (table: string, id: string) => { await direct(`/api/row/${table}/${id}`, 'DELETE'); refresh() }
  const add = async (table: string, name: string) => {
    if (!name.trim()) return
    await direct('/api/create', 'POST', { table, name: name.trim(), project_id: p.id }); refresh()
  }

  return <div className="main" style={{ maxWidth: 1180 }}>
    <div className="phead">
      <div style={{ flex: 1 }}><h1>Brands &amp; flows</h1>
        <div className="desc">Rename, reorder or remove. Brand order is the order used in every comparison.</div></div>
      <button className="btn" onClick={() => go(`config/${flows[0]?.id ?? ''}`)}>Attributes &amp; steps</button>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>
      <div>
        <div className="slab"><h2>Brands</h2><span className="hint">{brands.length}</span></div>
        <div className="card" style={{ padding: '4px 14px 12px' }}>
          {brands.map((b, i) => (
            <ConfigRow key={b.id} name={b.name} usage={imgPerBrand(b.id)}
              meta={<div style={{ flex: 1.2, minWidth: 0 }}><Field value={b.note ?? ''}
                onCommit={v => void queue(`/api/row/brands/${b.id}`, 'PATCH', { note: v })}
                placeholder="Coverage note…" /></div>}
              onRename={v => void queue(`/api/row/brands/${b.id}`, 'PATCH', { name: v }).then(() => setTimeout(refresh, 400))}
              onDelete={() => void del('brands', b.id)}
              onUp={i > 0 ? () => void move(brands, i, -1, 'brands') : undefined}
              onDown={i < brands.length - 1 ? () => void move(brands, i, 1, 'brands') : undefined} />
          ))}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
            <div style={{ flex: 1 }}><Field label="New brand" value={nb} onCommit={setNb} placeholder="Upstox" /></div>
            <button className="btn primary" onClick={() => { void add('brands', nb); setNb('') }}>Add</button>
          </div>
        </div>
      </div>
      <div>
        <div className="slab"><h2>Flows</h2><span className="hint">{flows.length}</span></div>
        <div className="card" style={{ padding: '4px 14px 12px' }}>
          {flows.map((f, i) => (
            <ConfigRow key={f.id} name={f.name} usage={stepsPerFlow(f.id)} usageLabel="steps"
              meta={<span className="chip quiet">{boot.attributes.filter(a => a.flow_id === f.id).length} attrs</span>}
              onRename={v => void queue(`/api/row/flows/${f.id}`, 'PATCH', { name: v }).then(() => setTimeout(refresh, 400))}
              onDelete={() => void del('flows', f.id)}
              onUp={i > 0 ? () => void move(flows, i, -1, 'flows') : undefined}
              onDown={i < flows.length - 1 ? () => void move(flows, i, 1, 'flows') : undefined} />
          ))}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}>
            <div style={{ flex: 1 }}><Field label="New flow" value={nf} onCommit={setNf} placeholder="Margin & Funds" /></div>
            <button className="btn primary" onClick={() => { void add('flows', nf); setNf('') }}>Add</button>
          </div>
        </div>
      </div>
    </div>
  </div>
}

// ---------------------------------------------------------------- removed / restore

/** Everything soft-deleted, with restore. Nothing in Vantage is ever destroyed. */
const Removed: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const p = boot.project!
  const [d, setD] = useState<Record<string, Row[]>>({})
  const load = useCallback(() => { get(`/api/removed?project=${p.id}`).then(setD).catch(() => {}) }, [p.id])
  useEffect(load, [load])

  const restore = async (table: string, id: string) => {
    await direct(`/api/restore/${table}/${id}`, 'POST', {}); load(); await reload(p.id)
  }
  const total = Object.values(d).reduce((a, v) => a + (v?.length ?? 0), 0)
  const label = (t: string, r: Row) =>
    t === 'images' ? (r.display_name || r.original_filename)
    : t === 'issues' ? r.title
    : r.flow_name ? `${r.name}  ·  ${r.flow_name}` : r.name

  return <div className="main" style={{ maxWidth: 1000 }}>
    <div className="phead"><div style={{ flex: 1 }}><h1>Removed</h1>
      <div className="desc">{total} removed. Nothing here has been destroyed — restoring puts it back
      everywhere it used to appear, with its history intact.</div></div>
      <button className="btn ghost" onClick={() => go('')}>← Project</button>
    </div>

    {!total && <Empty>Nothing removed.</Empty>}

    {d.images?.length ? <>
      <div className="slab"><h2>Images</h2><span className="hint">{d.images.length}</span></div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 14, marginBottom: 26 }}>
        {d.images.map(i => <div key={i.id}>
          <div style={{ opacity: .5 }}><Thumb img={i} h={190} /></div>
          <div className="mono" style={{ fontSize: 10, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {i.display_name || i.original_filename}</div>
          <button className="btn sm" style={{ marginTop: 5 }} onClick={() => void restore('images', i.id)}>Restore</button>
        </div>)}
      </div>
    </> : null}

    {(['brands', 'flows', 'steps', 'attributes', 'issues'] as const).map(t =>
      d[t]?.length ? <div key={t} style={{ marginBottom: 22 }}>
        <div className="slab"><h2>{t}</h2><span className="hint">{d[t].length}</span></div>
        <div className="card" style={{ padding: '4px 14px' }}>
          {d[t].map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0',
              borderBottom: '1px solid var(--line-2)' }}>
              <span style={{ flex: 1, opacity: .7 }}>{label(t, r)}</span>
              <span className="faint" style={{ fontSize: 11 }}>
                removed {new Date(r.deleted_at).toLocaleString('en-GB')}</span>
              <button className="btn sm" onClick={() => void restore(t, r.id)}>Restore</button>
            </div>
          ))}
        </div>
      </div> : null)}
  </div>
}

// ---------------------------------------------------------------- S10 issues

const Issues: React.FC<{ boot: Boot; reload: (p?: string) => void }> = ({ boot, reload }) => {
  const p = boot.project!
  const [status, setStatus] = useState(''); const [brand, setBrand] = useState('')
  const [title, setTitle] = useState('')
  const rows = boot.issues.filter(i => (!status || i.status === status) && (!brand || i.brand_id === brand))
  const count = (s: string) => boot.issues.filter(i => i.status === s).length
  const refresh = () => reload(p.id)

  const add = async () => {
    if (!title.trim()) return
    await direct('/api/issues', 'POST', { project_id: p.id, title: title.trim(), brand_id: brand || null })
    setTitle(''); refresh()
  }
  return <div className="main" style={{ maxWidth: 1180 }}>
    <div className="phead">
      <div style={{ flex: 1 }}><h1>Issues</h1>
        <div className="desc">Raised on a screen or standalone. Everything here is editable.</div></div>
      <select style={{ width: 150 }} value={brand} onChange={e => setBrand(e.target.value)}>
        <option value="">All brands</option>
        {boot.brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select>
      <select style={{ width: 130 }} value={status} onChange={e => setStatus(e.target.value)}>
        <option value="">All statuses</option>
        {['pending', 'wip', 'done', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}</select>
    </div>
    <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
      {['pending', 'wip', 'done', 'rejected'].map(s =>
        <span key={s} className={`chip${s === 'pending' && count(s) ? ' warn' : ''}`}>{s} {count(s)}</span>)}
      <span style={{ flex: 1 }} />
      <div style={{ width: 280 }}><Field value={title} onCommit={setTitle} placeholder="New issue…" /></div>
      <button className="btn primary" onClick={() => void add()}>Add</button>
    </div>
    {!rows.length ? <Empty>No issues match.</Empty> :
      <div className="card" style={{ padding: '4px 14px 10px' }}>
        {rows.map(i => (
          <div key={i.id} style={{ borderBottom: '1px solid var(--line-2)', padding: '10px 0' }}>
            <div style={{ display: 'flex', gap: 9, alignItems: 'center' }}>
              <div style={{ flex: 1 }}><Field value={i.title}
                onCommit={v => void queue(`/api/row/issues/${i.id}`, 'PATCH', { title: v })} /></div>
              <select style={{ width: 112 }} defaultValue={i.status}
                onChange={e => void queue(`/api/row/issues/${i.id}`, 'PATCH', { status: e.target.value }).then(() => setTimeout(refresh, 400))}>
                {['pending', 'wip', 'done', 'rejected'].map(s => <option key={s} value={s}>{s}</option>)}</select>
              <span className="chip quiet" style={{ whiteSpace: 'nowrap' }}>
                {boot.brands.find(b => b.id === i.brand_id)?.name ?? 'no brand'}</span>
              <span className="faint" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                {new Date(i.created_at).toLocaleDateString('en-GB')}</span>
              <button className="ibtn" title="Delete (recoverable)"
                onClick={async () => { await direct(`/api/row/issues/${i.id}`, 'DELETE'); refresh() }}>✕</button>
            </div>
            <div style={{ marginTop: 6 }}><Field value={i.body ?? ''} textarea
              onCommit={v => void queue(`/api/row/issues/${i.id}`, 'PATCH', { body: v })}
              placeholder="Detail…" /></div>
          </div>
        ))}
      </div>}
  </div>
}
